import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "button", "message", "spinner", "hiddenField"];
  static values = {
    schoolTokenSelector: String,
    packageSelector: String,
    packageIdSelector: String,
    emailSelector: String,
    studentEmailSelector: String,
    guardianEmailSelector: String,
    isBookingForSomeoneElseSelector: String,
    pupilIdSelector: String,
    classroomProgramIdSelector: String,
    validationEndpoint: String,
    depositButtonSelector: String
  };

  connect() {
    this.updateButtonState();

    // Listen for package selection changes on the document
    this.packageChangeHandler = (event) => {
      // Check if the changed element matches our package selectors
      const isPackageRadio = this.packageSelectorValue &&
        event.target.matches(this.packageSelectorValue);
      const isPackageIdField = this.packageIdSelectorValue &&
        event.target.matches(this.packageIdSelectorValue);
      const isClassroomProgramField = this.classroomProgramIdSelectorValue &&
        event.target.matches(this.classroomProgramIdSelectorValue);
      // Also listen for selected_program radio buttons (classroom flow)
      const isClassroomProgramRadio = event.target.name === 'selected_program' &&
        event.target.type === 'radio';

      if (isPackageRadio || isPackageIdField || isClassroomProgramField || isClassroomProgramRadio) {
        this.onPackageSelected();
      }
    };

    document.addEventListener('change', this.packageChangeHandler);

    // Also listen for custom events from other controllers (bubbles to document)
    this.packageSelectedHandler = () => {
      this.onPackageSelected();
    };
    document.addEventListener('packageSelected', this.packageSelectedHandler);
  }

  disconnect() {
    if (this.packageChangeHandler) {
      document.removeEventListener('change', this.packageChangeHandler);
    }
    if (this.packageSelectedHandler) {
      document.removeEventListener('packageSelected', this.packageSelectedHandler);
    }
  }

  onPackageSelected() {
    this.updateButtonState();
    
    // If there's a "no package" error message and a package is now selected, clear it
    if (this.hasMessageTarget && this.hasPackageSelected()) {
      const messageText = this.messageTarget.textContent || '';
      if (messageText.includes('Please select a package')) {
        this.hideMessage();
      }
    }
    
    // Dispatch event for parent controllers
    this.element.dispatchEvent(
      new CustomEvent("discountCodePackageSelected", {
        bubbles: true,
        detail: { hasPackage: this.hasPackageSelected() }
      })
    );
  }

  onInput(event) {
    this.updateButtonState();
    this.checkPackageSelection();
  }

  checkPackageSelection() {
    const code = this.inputTarget.value.trim();
    const hasPackage = this.hasPackageSelected();

    if (code && !hasPackage) {
      this.showMessage('Please select a package before applying discount code', 'error');
    } else if (!code) {
      this.hideMessage();
      // Clear the hidden field and re-enable deposit button
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = '';
      }
      this.handleDepositButton(false);
      
      this.element.dispatchEvent(
        new CustomEvent("discountCodeValidated", {
          bubbles: true,
          detail: {
            valid: false,
            cleared: true
          }
        })
      );
    } else if (hasPackage) {
      // Hide the "no package" error if they select a package while typing
      const messageText = this.messageTarget.textContent || '';
      if (messageText.includes('Please select a package')) {
        this.hideMessage();
      }
    }
  }

  async validate(event) {
    event.preventDefault();

    const code = this.inputTarget.value.trim();
    if (!code) {
      return;
    }

    if (!this.hasPackageSelected()) {
      this.showMessage('Please select a package before applying discount code', 'error');
      return;
    }

    // Show spinner, hide message
    this.spinnerTarget.style.display = 'block';
    this.messageTarget.style.display = 'none';
    this.buttonTarget.disabled = true;

    try {
      const params = this.buildValidationParams(code);
      const url = new URL(this.validationEndpointValue || '/us/discount-code/validate', window.location.origin);

      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
          url.searchParams.append(key, params[key]);
        }
      });

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        credentials: 'same-origin'
      });

      const data = await response.json();

      // Hide spinner
      this.spinnerTarget.style.display = 'none';
      this.updateButtonState();

      if (response.ok && data.valid) {
        // Show success message with discount details
        const discountText = data.formatted_discount ||
          `${data.discount_type === 'Percent' ? data.discount_amount + '%' : '$' + data.discount_amount} off`;
        this.showMessage(`✓ Discount applied: ${discountText}`, 'success');

        // Store the discount code ID
        if (data.discount_code_id && this.hasHiddenFieldTarget) {
          this.hiddenFieldTarget.value = data.discount_code_id;
        }

        // Disable deposit button when discount is applied
        this.handleDepositButton(true);

        // Dispatch success event
        this.element.dispatchEvent(
          new CustomEvent("discountCodeValidated", {
            bubbles: true,
            detail: {
              valid: true,
              discountCodeId: data.discount_code_id,
              discountAmount: data.discount_amount,
              discountType: data.discount_type,
              formattedDiscount: data.formatted_discount
            }
          })
        );
      } else {
        // Show error message
        this.showMessage(data.error || 'Invalid discount code', 'error');
        if (this.hasHiddenFieldTarget) {
          this.hiddenFieldTarget.value = '';
        }

        // Re-enable deposit button when discount is invalid
        this.handleDepositButton(false);

        // Dispatch error event
        this.element.dispatchEvent(
          new CustomEvent("discountCodeValidated", {
            bubbles: true,
            detail: {
              valid: false,
              error: data.error || 'Invalid discount code'
            }
          })
        );
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      this.spinnerTarget.style.display = 'none';
      this.updateButtonState();
      this.showMessage('Error validating code. Please try again.', 'error');
      if (this.hasHiddenFieldTarget) {
        this.hiddenFieldTarget.value = '';
      }
      
      // Re-enable deposit button on error
      this.handleDepositButton(false);
    }
  }

  buildValidationParams(code) {
    const params = {
      code: code,
      school_token: this.getSchoolToken(),
      package_id: this.getSelectedPackageId()
    };

    // Add optional email parameters
    if (this.emailSelectorValue) {
      const email = this.getSelectorValue(this.emailSelectorValue);
      if (email) params.email = email;
    }

    if (this.studentEmailSelectorValue) {
      const studentEmail = this.getSelectorValue(this.studentEmailSelectorValue);
      if (studentEmail) params.student_email = studentEmail;
    }

    if (this.guardianEmailSelectorValue) {
      const guardianEmail = this.getSelectorValue(this.guardianEmailSelectorValue);
      if (guardianEmail) params.guardian_email = guardianEmail;
    }

    if (this.isBookingForSomeoneElseSelectorValue) {
      const isBookingForSomeoneElse = this.getSelectorChecked(this.isBookingForSomeoneElseSelectorValue);
      if (isBookingForSomeoneElse !== null) {
        params.is_booking_for_someone_else = isBookingForSomeoneElse ? 'true' : 'false';
      }
    }

    if (this.classroomProgramIdSelectorValue) {
      const classroomProgramId = this.getSelectorValue(this.classroomProgramIdSelectorValue);
      if (classroomProgramId) params.classroom_program_id = classroomProgramId;
    }

    if (this.pupilIdSelectorValue) {
      const pupilId = this.getSelectorValue(this.pupilIdSelectorValue);
      if (pupilId) params.pupil_id = pupilId;
    }

    return params;
  }

  getSchoolToken() {
    if (this.schoolTokenSelectorValue) {
      const element = document.querySelector(this.schoolTokenSelectorValue);
      if (element) {
        return element.value || element.textContent?.trim();
      }
    }
    // Fallback to common selectors
    const fallback = document.querySelector('[name="driving_school_token"]') ||
                     document.getElementById('us-school-token');
    return fallback?.value || fallback?.textContent?.trim() || '';
  }

  getSelectedPackageId() {
    // Try package selector first (radio buttons)
    if (this.packageSelectorValue) {
      const selectedPackage = document.querySelector(this.packageSelectorValue);
      if (selectedPackage) {
        return selectedPackage.value;
      }
    }

    // Fallback to package ID selector (hidden field)
    if (this.packageIdSelectorValue) {
      const packageIdField = document.querySelector(this.packageIdSelectorValue);
      if (packageIdField && packageIdField.value) {
        return packageIdField.value;
      }
    }

    return null;
  }

  hasPackageSelected() {
    return this.getSelectedPackageId() !== null && this.getSelectedPackageId() !== '' && this.getSelectedPackageId() !== '0';
  }

  getSelectorValue(selector) {
    if (!selector) return null;
    const element = document.querySelector(selector);
    return element?.value || null;
  }

  getSelectorChecked(selector) {
    if (!selector) return null;
    const element = document.querySelector(selector);
    return element?.checked || null;
  }

  updateButtonState() {
    if (!this.hasButtonTarget) return;

    const hasPackage = this.hasPackageSelected();
    const hasCode = this.inputTarget.value.trim() !== '';

    this.buttonTarget.disabled = !(hasPackage && hasCode);
  }

  showMessage(text, type = 'info') {
    if (!this.hasMessageTarget) return;

    const colorClass = type === 'error' ? 'u-textRed' : type === 'success' ? 'u-textGreen' : 'u-textMuted';
    this.messageTarget.innerHTML = `<span class="${colorClass}">${text}</span>`;
    this.messageTarget.style.display = 'block';
    this.messageTarget.className = 'mt1';
  }

  hideMessage() {
    if (!this.hasMessageTarget) return;
    this.messageTarget.style.display = 'none';
    this.spinnerTarget.style.display = 'none';
  }

  handleDepositButton(disable) {
    // Try multiple selectors to find the deposit button
    const selectors = [];
    
    // If a specific selector is provided, use it first
    if (this.hasDepositButtonSelectorValue && this.depositButtonSelectorValue && this.depositButtonSelectorValue.trim() !== '') {
      selectors.push(this.depositButtonSelectorValue);
    }
    
    // Always try the default selector
    selectors.push('#pay-deposit-button');
    
    // Also try finding by data-action attribute
    selectors.push('[data-action*="payDepositPressed"]');
    
    let depositButton = null;
    for (const selector of selectors) {
      depositButton = document.querySelector(selector) || document.getElementById(selector.replace('#', ''));
      if (depositButton) {
        break;
      }
    }
    
    if (depositButton) {
      depositButton.disabled = disable;
    }
    
    // Show/hide the deposit unavailable message
    const depositMessage = document.getElementById('deposit-unavailable-message');
    if (depositMessage) {
      if (disable) {
        depositMessage.style.display = 'block';
      } else {
        depositMessage.style.display = 'none';
      }
    }
  }
}
